import { AtosaConfig } from './atosa';
import { FYIConfig } from './fyi-docs';
import { UserOverview } from './user';
import { Workpaper } from './workpaper';

export enum SubscriptionStatus {
    'ACTIVE' = 'ACTIVE',
    'TRIALING' = 'TRIALING',
    'INCOMPLETE' = 'INCOMPLETE',
    'INCOMPLETE_EXPIRED' = 'INCOMPLETE_EXPIRED',
    'PAST_DUE' = 'PAST_DUE',
    'CANCELED' = 'CANCELED',
    'UNPAID' = 'UNPAID',
    'PAUSED' = 'PAUSED',
}

export enum SubscriptionTier {
    'BASIC' = 'BASIC',
    'ESSENTIAL' = 'ESSENTIAL',
    'PROFESSIONAL' = 'PROFESSIONAL',
    'ENTERPRISE' = 'ENTERPRISE',
}

export enum SubscriptionDuration {
    'MONTHLY' = 'MONTHLY',
    'YEARLY' = 'YEARLY',
}

export enum SubscriptionScheduleType {
    'TIER_CHANGE' = 'TIER_CHANGE',
    'DURATION_CHANGE' = 'DURATION_CHANGE',
    'QUANTITY_DECREASE' = 'QUANTITY_DECREASE',
}

export type SubscriptionQuota = {
    firmId: string;
    procedureLimit: number | null;
    checklistLimit: number | null;
    procedureCount: number;
    checklistCount: number;
    workpaperPerEntityLimit: number;
    createdAt: Date;
    updatedAt: Date;
};

export type Firm =
    | {
          id: string;
          shortId: string;
          isSetup: boolean;
          name: string;
          createdAt: Date;
          updatedAt: Date;
          createdBy: UserOverview;
          admin: UserOverview;
          avatarHexColour?: string;
          integrations: {
              fyi?: FYIConfig;
              atosa?: AtosaConfig;
          };
          allowedIntegrations?: string[];
          // eslint-disable-next-line no-use-before-define
          subscription: Subscription | null;
          quota: SubscriptionQuota | null;
          hasEarlyAccess: boolean;
          subscriptionVersion: 1;
      }
    | {
          id: string;
          shortId: string;
          isSetup: boolean;
          name: string;
          createdAt: Date;
          updatedAt: Date;
          createdBy: UserOverview;
          admin: UserOverview;
          avatarHexColour?: string;
          integrations: {
              fyi?: FYIConfig;
              atosa?: AtosaConfig;
          };
          allowedIntegrations?: string[];
          // eslint-disable-next-line no-use-before-define
          subscription: SubscriptionV2 | null;
          hasEarlyAccess: boolean;
          subscriptionVersion: 2;
      };

export type Subscription = {
    firm: {
        id: string;
        name: string;
    };
    schedule: {
        stripeScheduleId: string;
        createdAt: Date;
        updatedAt: Date;
        tier: SubscriptionTier | null;
        tierScheduleDate: Date | null;
        duration: SubscriptionDuration | null;
        durationScheduleDate: Date | null;
        activeEntityAllocation: number | null;
        activeEntityAllocationScheduleDate: Date | null;
    } | null;
    status: SubscriptionStatus;
    tier: SubscriptionTier;
    duration: SubscriptionDuration;
    trialEnd: Date;
    annualBillingPeriodStart: Date;
    annualBillingPeriodEnd: Date;
    pricePerEntity: number;
    total: number;
    activeEntities: {
        allocation: number;
        count: number;
        remaining: number;
    };
    activeIndividuals: {
        allocation: number;
        count: number;
        remaining: number;
    };
    stripeCustomerId: string;
    stripeSubscriptionId: string;
    createdAt: Date;
    updatedAt: Date;
    nextInvoice: { date: Date; amount: number } | null;
    quota: SubscriptionQuota;
    isBespokeAgreement: boolean;
};

export type FirmEntryDescription = {
    id: string;
    firmId: string;
    description: string;
    sequence: number;
    createdAt: Date;
    updatedAt: Date;
};

export type FirmNotApplicableDescription = {
    id: string;
    firmId: string;
    description: string;
    sequence: number;
    createdAt: Date;
    updatedAt: Date;
};

export type FirmCompletedWork = {
    completedCount: number;
    avgCompletionDays: number;
    onSchedulePercent: number;
    overdueCount: number;
    avgOverdueDays: number;
};

export type FirmActiveWork = {
    activeCount: number;
    avgProgressPercent: number;
    onSchedulePercent: number;
    overdueCount: number;
    avgOverdueDays: number;
};

export type FirmInsights = {
    completedWork: FirmCompletedWork;
    activeWork: FirmActiveWork;
    overdueWorkpapers: {
        total: number;
        topResults: Workpaper[];
    };
};

// Subscription V2
// This is correlates with the stripe documentation: https://stripe.com/docs/billing/subscriptions/webhooks#state-changes
export type SubscriptionStatusV2 =
    | 'ACTIVE'
    | 'TRIALING'
    | 'INCOMPLETE'
    | 'INCOMPLETE_EXPIRED'
    | 'PAST_DUE'
    | 'CANCELED'
    | 'UNPAID'
    | 'PAUSED';

export type SubscriptionTierV2 = 'STARTER' | 'SILVER' | 'GOLD' | 'PLATINUM';

export type SubscriptionInterval = 'MONTHLY' | 'YEARLY';

export type SubscriptionV2 = {
    id: string;
    firmId: string;
    status: SubscriptionStatusV2;
    tier: SubscriptionTierV2;
    trialStart: Date | null;
    trialEnd: Date | null;
    currentPeriodEnd: Date;
    currentPeriodStart: Date;
    cancelledOn: Date | null;
    cancelAt: Date | null;
    nextQuotaRecount: Date | null;
    baseActiveEntityAllocation: number;
    totalActiveEntityAllocation: number;
    activeIndividualAllocation: number | null;
    activeEntities: number;
    stripeCustomerId: string;
    stripeSubscriptionId: string;
    interval: SubscriptionInterval;
    isBespoke: boolean;
    additionalEntitySlots: number;
    scheduledChange?: {
        stripeScheduleId: string;
        tier?: string;
        interval?: SubscriptionInterval;
        additionalEntitySlots?: number;
        updateScheduledAt: Date;
    } | null;
    createdAt: Date;
    updatedAt: Date;
    hasPaymentMethod: boolean;
};
